import React, { Suspense, lazy } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "./redux/store";
import ProtectedRoute from "./components/auth/ProtectedRoute";
import RequireAccess from "./components/auth/RequireAccess";
import MainLayout from "./components/MainLayout";
import LoadingState from "./components/ui/LoadingState";
import { selectAccess, selectModuleGrants } from "./redux/selectors/authSelectors";
import { landingPathFor } from "./utils/landingRoute";

// Pages are loaded on demand. Bundling all 23 screens into the initial download
// meant every user waited for the whole application before the login page
// appeared; now each screen arrives when it is first opened.
const LoginPage = lazy(() => import("./pages/Login"));
const ProfilePage = lazy(() => import("./pages/ProfilePage"));
const Tasks = lazy(() => import("./pages/Tasks"));
const AllContacts = lazy(() => import("./pages/AllContacts"));
const CustomerContact = lazy(() => import("./pages/CustomerContact"));
const CalendarDashboardRBC = lazy(() => import("./pages/Calender"));
const CompanyCalendar = lazy(() => import("./pages/CompanyCalendar"));
const TeamManagment = lazy(() => import("./pages/TeamManagment"));
const EditCluster = lazy(() => import("./components/team-managment/EditCluster"));
const EditClinic = lazy(() => import("./components/team-managment/EditClinic"));
const EditPractitioner = lazy(() => import("./components/team-managment/EditPractitioner"));
const Catalogue = lazy(() => import("./pages/Catalogue"));
const MenuItems = lazy(() => import("./pages/MenuItems"));
const MenuItemsList = lazy(() => import("./pages/MenuItemsList"));
const IngredientsList = lazy(() => import("./pages/IngredientsList"));
const Ingredients = lazy(() => import("./pages/Ingredients"));
const BookingsOrders = lazy(() => import("./pages/BookingsOrders"));
const EnrollmentResponse = lazy(() => import("./pages/EnrollmentResponse"));
const AbhaList = lazy(() => import("./pages/AbhaList"));
const Ecommerce = lazy(() => import("./pages/Ecommerce"));
const EcommerceProductForm = lazy(() => import("./pages/EcommerceProductForm"));
const CustomerEngagement = lazy(() => import("./pages/CustomerEngagement"));
const CustomerBlogManagement = lazy(() => import("./pages/CustomerBlogManagement"));
const VideoManagement = lazy(() => import("./pages/VideoManagement"));
const OffersAndDiscounts = lazy(() => import("./pages/OffersAndDiscounts"));
const Feedback = lazy(() => import("./pages/Feedback"));
const MealManagement = lazy(() => import("./pages/MealManagement"));
const WeeklyMealPlanner = lazy(() => import("./pages/WeeklyMealPlanner"));
const UserHistory = lazy(() => import("./pages/UserHistory"));
const Settings = lazy(() => import("./pages/Settings"));

function PageLoader() {
  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <LoadingState />
    </div>
  );
}

/**
 * The first screen this account can open — see utils/landingRoute.
 *
 * Read from the permission flags rather than fixed, because the Dashboard
 * placeholder this replaced was the only ungated landing page in the app.
 */
function useLandingPath() {
  const access = useSelector(selectAccess);
  const grants = useSelector(selectModuleGrants);
  return landingPathFor(access, grants);
}

// Only show children when NOT authed; if authed, go to their first screen.
function PublicOnlyRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const landing = useLandingPath();
  if (!isAuthenticated) return <>{children}</>;
  return <Navigate to={landing} replace />;
}

// Fallback: authed users go to their first screen, everyone else to login.
function CatchAll() {
  const { isAuthenticated } = useSelector((state: RootState) => state.auth);
  const landing = useLandingPath();
  return <Navigate to={isAuthenticated ? landing : "/login"} replace />;
}

/**
 * Sends the retired /dashboard address on to wherever the user now lands.
 *
 * Kept rather than deleted: the address is bookmarked, a browser holding the
 * previous JS bundle still navigates there after login, and Setup.tsx pushes to
 * it. Without this it would hit the catch-all — which is harmless, but this
 * states the intent instead of relying on it.
 */
function LegacyDashboardRedirect() {
  return <Navigate to={useLandingPath()} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<PublicOnlyRoute><LoginPage /></PublicOnlyRoute>} />

          {/* Authed-only shell */}
          <Route element={<ProtectedRoute />}>
            <Route element={<MainLayout />}>
              {/* The Dashboard placeholder was removed — this only forwards
                  old links to whatever the user's first real screen is. */}
              <Route path="/dashboard" element={<LegacyDashboardRedirect />} />

              <Route path="/profile" element={<ProfilePage />} />
              <Route path="/task" element={<Tasks />} />
              <Route path="/calendar" element={<CalendarDashboardRBC />} />
              <Route path="/company-calendar" element={<RequireAccess module="calendar"><CompanyCalendar /></RequireAccess>} />
              <Route path="/abha" element={<RequireAccess module="abha"><AbhaList /></RequireAccess>} />

              {/* Contacts */}
              <Route path="/contacts/all" element={<RequireAccess flag="customer_access"><AllContacts /></RequireAccess>} />
              <Route path="/leads/all" element={<RequireAccess flag="customer_access"><AllContacts /></RequireAccess>} />
              <Route path="/contact/add-customer" element={<RequireAccess flag="customer_access"><CustomerContact /></RequireAccess>} />
              <Route path="/contact/:id" element={<RequireAccess flag="customer_access"><CustomerContact /></RequireAccess>} />

              {/* Team management */}
              <Route path="/company/team-managment" element={<RequireAccess flag="team_management"><TeamManagment /></RequireAccess>} />
              <Route path="/edit/team-managment/cluster/:id" element={<RequireAccess flag="team_management"><EditCluster /></RequireAccess>} />
              <Route path="/edit/team-managment/clinic/:id" element={<RequireAccess flag="team_management"><EditClinic /></RequireAccess>} />
              <Route path="/edit/team-managment/practitioner/:id" element={<RequireAccess flag="team_management"><EditPractitioner /></RequireAccess>} />

              {/* Catalogue */}
              <Route path="/catalogue" element={<Navigate to="/catalogue/programs" replace />} />
              <Route path="/catalogue/:section" element={<RequireAccess flag="catalogue"><Catalogue /></RequireAccess>} />
              <Route path="/catalogue/:section/:action" element={<RequireAccess flag="catalogue"><Catalogue /></RequireAccess>} />
              <Route path="/catalogue/:section/:action/:id" element={<RequireAccess flag="catalogue"><Catalogue /></RequireAccess>} />

              {/* Meals */}
              {/* Meals used to route straight to the create form, with no list
                  and no way to open an existing item from a URL. */}
              <Route path="/remnutriMeals/meals" element={<RequireAccess flag="meals"><MenuItemsList /></RequireAccess>} />
              <Route path="/remnutriMeals/meals/create" element={<RequireAccess flag="meals"><MenuItems /></RequireAccess>} />
              <Route path="/remnutriMeals/meals/edit/:id" element={<RequireAccess flag="meals"><MenuItems /></RequireAccess>} />

              {/* Ingredient master — the IFCT nutrition table the meal form
                  searches. Same access flag as the rest of the Meals module. */}
              <Route path="/remnutriMeals/ingredients" element={<RequireAccess flag="meals"><IngredientsList /></RequireAccess>} />
              <Route path="/remnutriMeals/ingredients/create" element={<RequireAccess flag="meals"><Ingredients /></RequireAccess>} />
              <Route path="/remnutriMeals/ingredients/edit/:id" element={<RequireAccess flag="meals"><Ingredients /></RequireAccess>} />
              <Route path="/meal-management" element={<Navigate to="/meal-management/orders" replace />} />
              <Route path="/meal-management/:section" element={<RequireAccess flag="meals"><MealManagement /></RequireAccess>} />
              <Route path="/weekly-meal-planner" element={<RequireAccess flag="meals"><WeeklyMealPlanner /></RequireAccess>} />

              {/* User History — the console's own accounts. No access flag
                  covers this data, so the route is open to anyone signed in,
                  matching the sidebar entry and the ABHA precedent. */}
              <Route path="/user-history" element={<RequireAccess module="user-history"><UserHistory /></RequireAccess>} />

              {/* Bookings */}
              <Route path="/booking-orders" element={<RequireAccess flag="booking"><BookingsOrders /></RequireAccess>} />
              <Route path="/booking-orders/:enrollmentId" element={<RequireAccess flag="booking"><EnrollmentResponse /></RequireAccess>} />

              {/* Sales & marketing */}
              <Route path="/customer-engagement" element={<RequireAccess flag="marketing"><CustomerEngagement /></RequireAccess>} />
              <Route path="/customer-blog-managment" element={<RequireAccess flag="marketing"><CustomerBlogManagement /></RequireAccess>} />
              <Route path="/videos" element={<RequireAccess flag="marketing"><VideoManagement /></RequireAccess>} />
              <Route path="/offers-and-discounts" element={<RequireAccess flag="marketing"><OffersAndDiscounts /></RequireAccess>} />
              <Route path="/feedback" element={<RequireAccess flag="marketing"><Feedback /></RequireAccess>} />

              {/* E-commerce — no permission flag exists for this area yet */}
              <Route path="/ecommerce" element={<Ecommerce />} />
              <Route path="/ecommerce/create" element={<EcommerceProductForm />} />
              <Route path="/ecommerce/edit/:id" element={<EcommerceProductForm />} />

              <Route path="/settings" element={<RequireAccess flag="settings"><Settings /></RequireAccess>} />
            </Route>
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<CatchAll />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
